export const physiology2Module = {
  id: "physiology2",
  title: "Renal Physiology: Part 2",
  sections: [
    // ── RAAS ────────────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>The Renin-Angiotensin-Aldosterone System (RAAS)</h3>
      <p>The RAAS is the kidney's primary long-term blood pressure and volume regulator. Its activation and basic pathway were introduced in the anatomy modules. Here we focus on its physiology, clinical significance, and drug targets.</p>
      <p><strong>When is RAAS activated?</strong> Three main stimuli trigger renin release from juxtaglomerular (JG) cells:</p>
      <ol>
        <li><strong>↓ Afferent arteriole pressure</strong> (baroreceptor in JG cells) — hypotension, haemorrhage, renal artery stenosis</li>
        <li><strong>↓ NaCl delivery to macula densa</strong> — low GFR, low dietary Na⁺</li>
        <li><strong>Sympathetic activation</strong> (β₁ receptors on JG cells) — stress, heart failure</li>
      </ol>
      <p><strong>Angiotensin II (Ang II) effects:</strong></p>
      <ul>
        <li>Vasoconstriction (AT₁ receptors) → ↑ blood pressure</li>
        <li>Stimulates aldosterone from adrenal cortex → Na⁺/water retention</li>
        <li>Stimulates ADH from posterior pituitary → water retention</li>
        <li>Stimulates thirst</li>
        <li>Preferential constriction of the <strong>efferent arteriole</strong> → maintains GFR during hypotension</li>
      </ul>
      <p><strong>Key drug targets:</strong></p>
      <div class="highlight-box">RAAS drugs:
        <ul>
          <li><b class="purple-term">ACE inhibitors</b> (captopril, enalapril, ramipril): block conversion of Ang I → Ang II. Cause bradykinin accumulation → <strong>dry cough</strong> (in ~15%). Risk of <strong>hyperkalaemia</strong> (↓aldosterone) and AKI (dilate efferent arteriole). Reduce proteinuria → renoprotective in diabetic nephropathy.</li>
          <li><b class="purple-term">ARBs</b> (losartan, valsartan, candesartan): block AT₁ receptors. Similar effects to ACE inhibitors but <strong>no cough</strong> (bradykinin unaffected). Same hyperkalaemia risk.</li>
          <li><b class="purple-term">Direct renin inhibitors</b> (aliskiren): block renin directly. Limited use.</li>
          <li><b class="purple-term">Aldosterone antagonists</b> (spironolactone): covered in diuretics section.</li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "A patient with renal artery stenosis develops severe hypertension. Which drug class is commonly used but must be used cautiously — and why?",
      options: [
        "Loop diuretics — they reduce blood volume but worsen renin release",
        "ACE inhibitors — they block Ang II, which was maintaining GFR by constricting the efferent arteriole; removing it can cause AKI",
        "Beta-blockers — they stimulate JG cell renin release, worsening the problem",
        "Thiazides — they dilate the afferent arteriole, reducing filtration pressure",
      ],
      correct: 1,
      explanation:
        "In bilateral renal artery stenosis (or stenosis in a single kidney), GFR is maintained by Ang II-driven efferent arteriole constriction. ACE inhibitors block Ang II production, dilating the efferent arteriole — GFR collapses and AKI can occur. ACE inhibitors are therefore contraindicated in bilateral renal artery stenosis.",
    },
    {
      type: "question",
      text: "Why do ACE inhibitors cause a dry cough in some patients?",
      options: [
        "They reduce angiotensin II, causing pulmonary vasoconstriction",
        "They block the breakdown of bradykinin, which accumulates and irritates airway receptors",
        "They reduce aldosterone, causing pulmonary oedema",
        "They increase prostaglandin synthesis in bronchial tissue",
      ],
      correct: 1,
      explanation:
        "ACE (angiotensin-converting enzyme) not only converts Ang I to Ang II, it also degrades bradykinin. ACE inhibitors block both actions — bradykinin accumulates, irritating airway C-fibres and causing a persistent dry cough in ~10–15% of patients. ARBs do not affect bradykinin and therefore do not cause this side effect.",
    },
    {
      type: "question",
      text: "Which receptor on juxtaglomerular cells responds to sympathetic stimulation to trigger renin release?",
      options: ["α₁ receptor", "β₁ receptor", "AT₁ receptor", "V2 receptor"],
      correct: 1,
      explanation:
        "Juxtaglomerular cells express β₁ adrenergic receptors. Sympathetic activation (e.g. in heart failure, haemorrhage, or exercise) stimulates these receptors, triggering renin release and activating the RAAS. This is why β-blockers reduce renin levels and have antihypertensive effects.",
    },

    // ── ADH / Vasopressin ───────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>ADH (Antidiuretic Hormone / Vasopressin)</h3>
      <p>ADH (also called <b class="purple-term">vasopressin</b> or AVP) is synthesised in the <strong>supraoptic and paraventricular nuclei</strong> of the hypothalamus, stored and released from the <strong>posterior pituitary</strong>.</p>
      <p><strong>Stimuli for ADH release:</strong></p>
      <ul>
        <li><b>↑ Plasma osmolality</b> (main stimulus) — detected by osmoreceptors in the hypothalamus. Even a 1–2% rise triggers ADH release.</li>
        <li><b>↓ Blood volume or pressure</b> — detected by baroreceptors in the carotid sinus and aorta (less sensitive than osmoreceptors)</li>
        <li>Pain, nausea, some drugs (morphine, carbamazepine)</li>
      </ul>
      <p><strong>Actions:</strong></p>
      <ul>
        <li><b class="purple-term">V2 receptors</b> on collecting duct principal cells → cAMP → phosphorylation and insertion of <b class="purple-term">AQP2</b> water channels → water reabsorption → concentrated urine (up to ~1200 mOsm)</li>
        <li><b class="purple-term">V1 receptors</b> on vascular smooth muscle → vasoconstriction (at pharmacological doses)</li>
      </ul>
      <p><strong>Diabetes insipidus (DI):</strong> inability to concentrate urine → polyuria + dilute urine + hypernatraemia.</p>
      <ul>
        <li><b class="purple-term">Central DI</b>: ↓ADH production (head injury, pituitary surgery, tumour). Treatment: <strong>desmopressin</strong> (synthetic V2 agonist)</li>
        <li><b class="purple-term">Nephrogenic DI</b>: kidneys fail to respond to ADH (lithium toxicity, hypercalcaemia, genetic V2/AQP2 mutations). Treatment: <strong>thiazides</strong> (paradoxical — reduce ECF volume → compensatory PCT reabsorption → less water reaching the CD), low-Na/protein diet, NSAIDs (↓GFR)</li>
      </ul>
      <p><strong>SIADH</strong> (Syndrome of Inappropriate ADH): excess ADH → inappropriate water retention → <strong>euvolaemic hyponatraemia</strong> + concentrated urine (U<sub>osm</sub> > P<sub>osm</sub>).</p>
      <ul>
        <li>Causes: SCLC (small-cell lung cancer), CNS disease (meningitis, stroke), pulmonary disease (pneumonia, TB), drugs (SSRIs, carbamazepine, cyclophosphamide)</li>
        <li>Treatment: fluid restriction; tolvaptan/conivaptan (V2 receptor antagonists) in severe cases</li>
      </ul>`,
    },
    {
      type: "question",
      text: "A 60-year-old smoker presents with hyponatraemia, concentrated urine, and no signs of dehydration or oedema. Serum osmolality is low but urine osmolality is high. What is the most likely diagnosis?",
      options: [
        "Central diabetes insipidus",
        "Nephrogenic diabetes insipidus",
        "SIADH",
        "Primary polydipsia",
      ],
      correct: 2,
      explanation:
        "SIADH presents with euvolaemic hyponatraemia (no oedema, no dehydration), low plasma osmolality, and inappropriately concentrated urine (U(osm) > P(osm)). In a smoker, the most common cause is small-cell lung cancer (SCLC), which ectopically secretes ADH. Other causes include CNS disease and drugs.",
    },
    {
      type: "question",
      text: "A patient with central diabetes insipidus is prescribed desmopressin. Why does this treat the condition?",
      options: [
        "Desmopressin is a V1 agonist that causes vasoconstriction, raising blood pressure",
        "Desmopressin is a synthetic V2 agonist that restores AQP2 insertion in collecting duct principal cells, enabling water reabsorption",
        "Desmopressin inhibits prostaglandins, reducing afferent arteriole dilation",
        "Desmopressin blocks NCC in the DCT, reducing water loss",
      ],
      correct: 1,
      explanation:
        "Central DI is caused by insufficient ADH production. Desmopressin is a synthetic analogue of ADH that acts selectively on V2 receptors on collecting duct principal cells. It triggers cAMP production, phosphorylating and inserting AQP2 water channels into the apical membrane — restoring water reabsorption and urine concentration.",
    },
    {
      type: "question",
      text: "Why are thiazide diuretics paradoxically used to treat nephrogenic diabetes insipidus?",
      options: [
        "They block V2 receptors, sensitising the collecting duct to ADH",
        "They reduce ECF volume, causing compensatory PCT reabsorption, so less filtrate reaches the collecting duct and less water is lost",
        "They increase aldosterone, enhancing Na⁺ reabsorption in the collecting duct",
        "They activate AQP2 channels via a cAMP-independent pathway",
      ],
      correct: 1,
      explanation:
        "Thiazides reduce ECF volume by blocking NCC. The resultant volume depletion activates the PCT to reabsorb more Na⁺ and water (up to 65%). Less filtrate reaches the collecting duct — even though AQP2 channels remain absent, there is simply less volume to lose as urine. This paradoxically reduces polyuria in nephrogenic DI.",
    },

    // ── Aldosterone ─────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Aldosterone</h3>
      <p>Aldosterone is a mineralocorticoid produced by the <strong>zona glomerulosa</strong> of the adrenal cortex. Its main stimuli are <strong>Ang II</strong> (the primary stimulus), <strong>hyperkalaemia</strong> (directly stimulates the zona glomerulosa), and (mildly) hyponatraemia and ACTH.</p>
      <p><strong>Mechanism of action:</strong> Aldosterone crosses the cell membrane of collecting duct principal cells and binds to the <b class="purple-term">mineralocorticoid receptor (MR)</b>. The aldosterone-MR complex enters the nucleus and increases transcription of:
      <ul>
        <li><b class="purple-term">ENaC</b> (apical Na⁺ channels) — more Na⁺ enters the cell from the lumen</li>
        <li><b class="purple-term">Na⁺/K⁺-ATPase</b> (basolateral) — pumps Na⁺ out and K⁺ in</li>
        <li><b class="purple-term">ROMK</b> (apical K⁺ channels) — K⁺ secreted into the lumen</li>
      </ul>
      Net result: Na⁺ and water retained → ↑ blood pressure; K⁺ and H⁺ secreted → <strong>hypokalaemia</strong> and <strong>metabolic alkalosis</strong>.</p>
      <p><strong>Primary hyperaldosteronism (Conn's syndrome)</strong>: autonomous aldosterone secretion (usually an adrenal adenoma). Presents with:
      <ul>
        <li>Hypertension (Na⁺ retention)</li>
        <li>Hypokalaemia (K⁺ secretion)</li>
        <li>Metabolic alkalosis</li>
        <li>Low renin (aldosterone is not driven by Ang II — feedback suppresses renin)</li>
      </ul>
      Treatment: surgical resection of adenoma; or spironolactone (aldosterone antagonist).</p>
      <p><strong>Addison's disease</strong> (adrenocortical insufficiency): lack of aldosterone and cortisol → Na⁺ loss, K⁺ retention, hypotension, hyperkalaemia, and metabolic acidosis.</p>`,
    },
    {
      type: "question",
      text: "A patient has hypertension, hypokalaemia, metabolic alkalosis, and a suppressed renin level. What is the most likely diagnosis?",
      options: [
        "Secondary hyperaldosteronism from renal artery stenosis",
        "Primary hyperaldosteronism (Conn's syndrome)",
        "SIADH",
        "Addison's disease",
      ],
      correct: 1,
      explanation:
        "Primary hyperaldosteronism (Conn's syndrome) presents with hypertension, hypokalaemia, and metabolic alkalosis due to autonomous aldosterone secretion. Because aldosterone is secreted independently of the RAAS, renin is suppressed by negative feedback (high aldosterone → no need for Ang II to drive it). Renal artery stenosis causes secondary hyperaldosteronism where renin is elevated, not suppressed.",
    },
    {
      type: "question",
      text: "Why does hyperaldosteronism cause metabolic alkalosis?",
      options: [
        "Aldosterone increases HCO₃⁻ reabsorption in the PCT",
        "Aldosterone increases H⁺ secretion by intercalated cells and drives H⁺ into cells as K⁺ leaves, generating excess HCO₃⁻",
        "Aldosterone blocks carbonic anhydrase, reducing H⁺ production",
        "Aldosterone increases Na⁺/H⁺ exchange in the thick ascending limb",
      ],
      correct: 1,
      explanation:
        "Aldosterone stimulates both Na⁺ reabsorption (ENaC) and K⁺ secretion. As intracellular K⁺ falls (hypokalaemia), H⁺ shifts into cells in exchange, lowering extracellular H⁺ concentration (alkalosis). Aldosterone also stimulates H⁺ secretion by type A intercalated cells. Together these cause hypokalaemic metabolic alkalosis.",
    },

    // ── ANP & BNP ────────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Atrial Natriuretic Peptide (ANP) & BNP</h3>
      <p><b class="purple-term">Atrial natriuretic peptide (ANP)</b> is released from atrial cardiomyocytes when the atrial wall is <strong>stretched</strong> by increased blood volume or pressure. <b class="purple-term">Brain natriuretic peptide (BNP)</b> is released from ventricular cardiomyocytes under similar conditions.</p>
      <p>ANP and BNP oppose the RAAS and act as natural volume-lowering hormones:</p>
      <ul>
        <li>Dilate the <strong>afferent arteriole</strong> and constrict the <strong>efferent arteriole</strong> → ↑ GFR → more filtration</li>
        <li>Inhibit Na⁺ reabsorption in the <strong>collecting duct</strong> (block ENaC)</li>
        <li>Inhibit renin and aldosterone secretion</li>
        <li>Cause systemic vasodilation → ↓ blood pressure</li>
      </ul>
      <p>Net effect: <strong>↑ urinary Na⁺ and water excretion (natriuresis and diuresis) → ↓ blood volume → ↓ blood pressure</strong>.</p>
      <p><strong>Clinical relevance of BNP:</strong> BNP is used as a <strong>biomarker for heart failure</strong>. Elevated BNP (or NT-proBNP) in a dyspnoeic patient strongly supports heart failure as the diagnosis.</p>
      <p><b class="purple-term">Sacubitril/valsartan (Entresto)</b> is a combination drug used in heart failure: valsartan blocks AT₁ receptors (ARB), while sacubitril inhibits <b class="purple-term">neprilysin</b> — the enzyme that breaks down ANP and BNP. This prolongs the actions of natriuretic peptides, reducing cardiac preload and afterload.</p>`,
    },
    {
      type: "question",
      text: "What triggers ANP release and what is its net effect on the kidney?",
      options: [
        "Low blood pressure; ANP reduces GFR and increases Na⁺ reabsorption",
        "Atrial wall stretch from high blood volume; ANP increases GFR and promotes Na⁺ and water excretion",
        "High renin; ANP blocks angiotensin II production in the lung",
        "Low plasma osmolality; ANP increases ADH release",
      ],
      correct: 1,
      explanation:
        "ANP is released when atrial pressure/volume is high (atrial wall stretch). In the kidney, it dilates the afferent and constricts the efferent arteriole (↑GFR), inhibits ENaC in the collecting duct (↓Na⁺ reabsorption), and inhibits renin and aldosterone. The net effect is natriuresis and diuresis, reducing blood volume and pressure.",
    },
    {
      type: "question",
      text: "Why is BNP measured in a patient presenting with acute shortness of breath?",
      options: [
        "High BNP indicates renal artery stenosis as the cause of dyspnoea",
        "High BNP reflects ventricular wall stress and is a sensitive biomarker for heart failure",
        "BNP measures glomerular filtration rate in dyspnoeic patients",
        "BNP is released by the lung in pulmonary embolism",
      ],
      correct: 1,
      explanation:
        "BNP (or NT-proBNP) is released from ventricular cardiomyocytes in response to wall stress — high filling pressures and ventricular dilation in heart failure. Elevated BNP in a dyspnoeic patient is a sensitive and specific marker for heart failure, helping differentiate it from pulmonary causes of dyspnoea.",
    },

    // ── EPO & Vitamin D ──────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Erythropoietin (EPO) & Vitamin D Activation</h3>
      <p>The kidneys perform two important endocrine functions beyond blood pressure regulation:</p>
      <p><strong>1. Erythropoietin (EPO) production:</strong></p>
      <p>EPO is produced by <b class="purple-term">peritubular fibroblasts</b> in the renal cortex in response to <strong>hypoxia</strong> (mediated by HIF-1α — hypoxia-inducible factor 1-alpha). EPO travels to the bone marrow and stimulates the proliferation and differentiation of red blood cell precursors (erythropoiesis).</p>
      <p>In <strong>chronic kidney disease (CKD)</strong>, the number of functioning peritubular fibroblasts falls → EPO production decreases → <b class="purple-term">normocytic normochromic anaemia of CKD</b>. Treatment: recombinant EPO analogues (epoetin alfa, darbepoetin alfa).</p>
      <p><strong>2. Activation of Vitamin D:</strong></p>
      <p>Vitamin D must be activated in two steps before it becomes biologically active:</p>
      <ol>
        <li><strong>Skin</strong>: UV light converts 7-dehydrocholesterol → <strong>cholecalciferol (vitamin D₃)</strong></li>
        <li><strong>Liver</strong>: 25-hydroxylase → <strong>25-hydroxycholecalciferol (calcidiol)</strong> — the major circulating form, measured in blood tests</li>
        <li><strong>Kidney (PCT)</strong>: <b class="purple-term">1α-hydroxylase</b> → <strong>1,25-dihydroxycholecalciferol (calcitriol)</strong> — the fully active form. PTH stimulates this enzyme; phosphate and calcitriol itself inhibit it.</li>
      </ol>
      <p>Active calcitriol increases Ca²⁺ and phosphate absorption from the gut, and Ca²⁺ reabsorption from the kidney.</p>
      <p>In CKD: ↓ 1α-hydroxylase → ↓ calcitriol → ↓ gut Ca²⁺ absorption → <strong>hypocalcaemia</strong> → secondary hyperparathyroidism → PTH stimulates bone resorption → <b class="purple-term">renal osteodystrophy</b>. Treatment: calcitriol supplements, phosphate binders.</p>`,
    },
    {
      type: "question",
      text: "Why do patients with chronic kidney disease develop anaemia?",
      options: [
        "CKD causes haemolysis of red blood cells",
        "CKD causes iron deficiency due to poor gut absorption",
        "Loss of peritubular fibroblasts in the damaged kidney reduces EPO production, impairing red cell production",
        "CKD causes folate deficiency due to increased urinary loss",
      ],
      correct: 2,
      explanation:
        "EPO is produced by peritubular fibroblasts in the renal cortex in response to hypoxia. As CKD progresses, functional renal tissue is lost, including these fibroblasts. EPO production falls, leading to a normocytic normochromic anaemia. This is treated with recombinant EPO analogues (epoetin alfa, darbepoetin).",
    },
    {
      type: "question",
      text: "A patient with advanced CKD develops bone pain and secondary hyperparathyroidism. What is the underlying mechanism?",
      options: [
        "CKD causes excess calcitriol → hypercalcaemia → PTH suppression → bone disease",
        "CKD reduces renal 1α-hydroxylase activity → low calcitriol → low Ca²⁺ absorption → PTH rises to compensate → bone resorption",
        "CKD causes phosphate wasting → hypophosphataemia → poor bone mineralisation",
        "CKD causes excess aldosterone → Ca²⁺ loss in urine → hypocalcaemia",
      ],
      correct: 1,
      explanation:
        "In CKD, the kidney's 1α-hydroxylase activity is reduced, limiting conversion of calcidiol to active calcitriol. Low calcitriol decreases gut Ca²⁺ absorption → hypocalcaemia → the parathyroid glands secrete more PTH (secondary hyperparathyroidism). Chronically elevated PTH drives bone resorption, causing renal osteodystrophy.",
    },

    // ── Sodium & Water ───────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Sodium and Water Balance — Hypo- and Hypernatraemia</h3>
      <p>Sodium is the principal determinant of <strong>extracellular fluid (ECF) volume and osmolality</strong> (normal serum Na⁺ 135–145 mmol/L). The kidney regulates Na⁺ balance through aldosterone, and water balance through ADH.</p>
      <p><b class="purple-term">Hyponatraemia</b> (Na⁺ < 135 mmol/L): always due to excess water relative to Na⁺.
      <br/>Categorised by volume status:</p>
      <ul>
        <li><strong>Hypovolaemic</strong> (dehydrated): Na⁺ and water both lost but more Na⁺ (diarrhoea, vomiting, diuretics, Addison's)</li>
        <li><strong>Euvolaemic</strong> (normal volume): water retained without Na⁺ gain (SIADH, hypothyroidism, psychogenic polydipsia)</li>
        <li><strong>Hypervolaemic</strong> (oedematous): Na⁺ and water both retained but more water (heart failure, cirrhosis, nephrotic syndrome)</li>
      </ul>
      <p>Symptoms depend on the rate of fall: acute hyponatraemia causes cerebral oedema → headache, confusion, seizures. <strong>Overly rapid correction</strong> of chronic hyponatraemia causes <b class="purple-term">osmotic demyelination syndrome</b> (central pontine myelinolysis) — permanent brainstem damage. Correct no faster than 8–10 mmol/L per 24 hours.</p>
      <p><b class="purple-term">Hypernatraemia</b> (Na⁺ > 145 mmol/L): always due to water deficit relative to Na⁺ (less commonly, Na⁺ excess). Causes: diabetes insipidus, insensible water loss (fever, burns), insufficient water intake, osmotic diuresis. Treatment: gradual free water replacement (oral or hypotonic IV).</p>`,
    },
    {
      type: "question",
      text: "A patient with severe heart failure has hyponatraemia, peripheral oedema, and elevated BNP. What type of hyponatraemia is this?",
      options: [
        "Hypovolaemic hyponatraemia — they need IV saline",
        "Euvolaemic hyponatraemia from SIADH",
        "Hypervolaemic hyponatraemia — excess total body water dilutes serum Na⁺",
        "Pseudohyponatraemia from hyperlipidaemia",
      ],
      correct: 2,
      explanation:
        "In heart failure, reduced cardiac output activates the RAAS and ADH (despite total body Na⁺ and water being elevated). Na⁺ and water are both retained, but water is retained in excess, diluting serum Na⁺. The patient is oedematous (hypervolaemic hyponatraemia). Treatment is fluid restriction and diuretics, not saline.",
    },
    {
      type: "question",
      text: "Why is rapid correction of chronic hyponatraemia dangerous?",
      options: [
        "Rapid correction causes cerebral oedema by pulling water into brain cells",
        "Rapid correction causes osmotic demyelination syndrome as myelin sheaths are damaged by rapid osmotic shifts",
        "Rapid correction causes seizures from hypercalcaemia",
        "Rapid correction overloads the kidneys, causing AKI",
      ],
      correct: 1,
      explanation:
        "In chronic hyponatraemia, brain cells adapt to the low osmotic environment by extruding organic osmolytes. If Na⁺ is corrected too rapidly, the plasma becomes hypertonic relative to brain cells — water is drawn out, causing osmotic demyelination (central pontine myelinolysis). This causes irreversible brainstem damage: dysphagia, dysarthria, quadriplegia. Correct no faster than 8–10 mmol/L/24h.",
    },

    // ── Potassium ────────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Potassium Balance — Hypo- and Hyperkalaemia</h3>
      <p>Potassium is the main intracellular cation (ICF ~140 mmol/L; ECF ~4 mmol/L). Small changes in ECF K⁺ have large effects on the resting membrane potential of excitable cells (heart, muscle, nerves).</p>
      <p><strong>Distribution between ICF and ECF is shifted by:</strong></p>
      <ul>
        <li><b>Insulin</b>: drives K⁺ into cells (Na⁺/K⁺-ATPase activation)</li>
        <li><b>Catecholamines (β₂)</b>: drive K⁺ into cells</li>
        <li><b>Alkalosis</b>: H⁺ leaves cells, K⁺ enters → ↓ serum K⁺</li>
        <li><b>Acidosis</b>: H⁺ enters cells, K⁺ leaves → ↑ serum K⁺</li>
      </ul>
      <p><b class="purple-term">Hypokalaemia</b> (K⁺ < 3.5 mmol/L):
      <br/>Causes: vomiting/NG losses (H⁺ loss → alkalosis → K⁺ into cells), diarrhoea, loop/thiazide diuretics, hyperaldosteronism.
      <br/>ECG: flattened T waves → prominent U waves → prolonged QT → risk of torsades de pointes.
      <br/>Treatment: oral/IV K⁺ replacement, treat underlying cause.</p>
      <p><b class="purple-term">Hyperkalaemia</b> (K⁺ > 5.5 mmol/L):
      <br/>Causes: renal failure (can't excrete K⁺), ACE inhibitors/ARBs, K⁺-sparing diuretics, tissue destruction (rhabdomyolysis, haemolysis), metabolic acidosis, Addison's disease.
      <br/>ECG changes (in order of severity): <strong>peaked (tented) T waves</strong> → prolonged PR → wide QRS → sine wave → VF/asystole.
      <br/>Treatment:</p>
      <ul>
        <li><b class="purple-term">Calcium gluconate</b>: stabilises cardiac membrane (does not lower K⁺) — first line if ECG changes present</li>
        <li><b class="purple-term">Insulin + glucose</b>: drives K⁺ into cells (temporary)</li>
        <li><b class="purple-term">Sodium bicarbonate</b>: alkalinises → K⁺ into cells (if acidotic)</li>
        <li><b class="purple-term">Salbutamol (nebulised)</b>: β₂ agonist → K⁺ into cells</li>
        <li><b class="purple-term">Sodium resonium / patiromer</b>: K⁺ binders → GI excretion (removes K⁺)</li>
        <li><b class="purple-term">Dialysis</b>: definitive removal in severe cases</li>
      </ul>`,
    },
    {
      type: "question",
      text: "A patient in the emergency department with a K⁺ of 7.2 mmol/L has peaked T waves on ECG. What is the immediate first step?",
      options: [
        "Give insulin and glucose to drive K⁺ into cells",
        "Give IV calcium gluconate to stabilise the cardiac membrane",
        "Give sodium bicarbonate to alkalinise the blood",
        "Start haemodialysis immediately",
      ],
      correct: 1,
      explanation:
        "When there are ECG changes from hyperkalaemia (peaked T waves, prolonged PR, wide QRS), the immediate priority is to stabilise the cardiac membrane with IV calcium gluconate. It acts within minutes by raising the threshold potential, protecting the heart from arrhythmia. It does not lower K⁺ — subsequent steps (insulin/glucose, salbutamol, resonium, dialysis) are needed to actually reduce K⁺.",
    },
    {
      type: "question",
      text: "Why does metabolic acidosis cause hyperkalaemia?",
      options: [
        "Acidosis activates aldosterone, causing K⁺ secretion from cells",
        "Acidosis stimulates the Na⁺/K⁺-ATPase, moving K⁺ out of cells",
        "In acidosis, excess H⁺ enters cells in exchange for K⁺ moving out, raising serum K⁺",
        "Acidosis directly inhibits the kidney's ability to filter K⁺",
      ],
      correct: 2,
      explanation:
        "In metabolic acidosis, the high extracellular H⁺ concentration drives H⁺ into cells to be buffered by intracellular proteins. To maintain electrical neutrality, K⁺ moves out of the cells into the ECF — raising serum K⁺. This is why diabetic ketoacidosis (DKA) patients can have high serum K⁺ despite total body K⁺ depletion.",
    },
  ],
};
